import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import YearJumpCalendar from "./YearJumpCalendar";

/**
 * Positions the year-jump calendar on document.body so modals and overflow
 * do not clip it.
 */
const YearJumpPopover = ({ open, anchorRef, value, min, max, onSelect, onClose, today }) => {
  const popRef = useRef(null);
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }

    const update = () => {
      const anchor = anchorRef?.current;
      const pop = popRef.current;
      if (!anchor || !pop) return;

      const rect = anchor.getBoundingClientRect();
      const width = pop.offsetWidth;
      const height = pop.offsetHeight;
      let top = rect.bottom + 6;
      let left = rect.left;

      if (top + height > window.innerHeight - 8 && rect.top - height - 6 > 8) {
        top = rect.top - height - 6;
      }
      left = Math.min(Math.max(8, left), Math.max(8, window.innerWidth - width - 8));
      if (top < 8) top = 8;

      setPosition({ top, left });
    };

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, anchorRef, value]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      const target = event.target;
      if (popRef.current?.contains(target)) return;
      if (anchorRef?.current?.contains(target)) return;
      onClose();
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose, anchorRef]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={popRef}
      style={{
        position: "fixed",
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        zIndex: 130,
        visibility: position ? "visible" : "hidden",
      }}
    >
      <YearJumpCalendar value={value} min={min} max={max} today={today} onSelect={onSelect} />
    </div>,
    document.body,
  );
};

export default YearJumpPopover;
